/** Shared type definitions for the tracking script. */
import type { GoogleSessionDetails } from "./google-session-data.js";

export interface TrackingRule {
  readonly id: string;
  readonly eventName: string;
  readonly urlPattern?: string;
  readonly matchType?: "exact" | "starts_with" | "contains" | "wildcard";
  readonly triggerType?: "pageview" | "click" | "form_submit" | "code";
  readonly elementText?: string;
  readonly cssSelector?: string;
  readonly selectorTagName?: string;
  readonly selectorHref?: string;
  readonly eventValue?: number;
  readonly currency?: string;
  readonly detectEmail?: boolean;
  readonly oncePerVisitor?: boolean;
  /** Platforms this rule fans out to; null/absent = fire all configured */
  readonly platforms?: readonly string[] | null;
}

export interface PlatformConfig {
  /** Shopify app pixel owns standard events; the theme sends matching context. */
  readonly shopify?: { readonly appCollector: boolean };
  readonly facebook?: {
    readonly pixelId?: string;
    /** Canonical -> renamed event names (pixel must mirror CAPI renames) */
    readonly renameMap?: Record<string, string>;
    readonly blockedEvents?: readonly string[];
  };
  readonly google_ads?: {
    readonly conversionId: string;
    readonly conversionLabel?: string;
    /** Per-event tag labels (canonical event name -> label) for multi-source
     *  WEBPAGE actions; wins over the destination-level conversionLabel */
    readonly eventLabels?: Readonly<Record<string, string>>;
  };
  readonly bing_ads?: {
    readonly tagId: string;
  };
  readonly linkedin?: {
    readonly partnerId: string;
    readonly conversionId?: string;
    /** Canonical event -> numeric lintrk conversion id (from event mapping) */
    readonly conversionMap?: Record<string, string>;
    /** Opt-in connector toggle: capture PLAINTEXT first/last name for
     * LinkedIn's probabilistic userInfo matching (LinkedIn-only fields) */
    readonly collectPlaintextNames?: boolean;
  };
  readonly tiktok?: {
    readonly pixelCode: string;
    /** Canonical -> renamed event names (pixel must mirror Events API renames) */
    readonly renameMap?: Record<string, string>;
  };
  readonly ga4?: {
    readonly measurementId: string;
    /** Events fired client-side via gtag (hybrid channel "pixel") */
    readonly pixelEvents?: ReadonlyArray<{ name: string; ga4Name: string }>;
  };
  readonly snapchat?: {
    readonly pixelCode: string;
    /** Canonical -> renamed event names (pixel must mirror CAPI renames) */
    readonly renameMap?: Record<string, string>;
  };
  readonly pinterest?: {
    /** Pinterest Conversion Tag id (separate from the ad account id) */
    readonly tagId: string;
    /** Canonical -> renamed event names (tag must mirror CAPI renames) */
    readonly renameMap?: Record<string, string>;
  };
  readonly openai_ads?: {
    readonly pixelId: string;
    readonly renameMap?: Readonly<Record<string, string>>;
    readonly blockedEvents?: readonly string[];
  };
  readonly reddit?: {
    readonly pixelId: string;
    /** Canonical -> renamed event names (pixel must mirror CAPI renames) */
    readonly renameMap?: Record<string, string>;
  };
}

export interface ConsentConfig {
  readonly autoConsentReading: boolean;
  readonly defaultAnalyticsConsent: string;
  readonly defaultMarketingConsent: string;
  readonly noCookieOnDenied: boolean;
  readonly removeUrlParams: string | null;
}

export interface CookieValues {
  readonly fbp?: string | null;
  readonly fbc?: string | null;
  readonly externalId?: string | null;
}

export interface Config {
  readonly trackingEnabled: boolean;
  /** Auto-bridge GA4 dataLayer e-commerce pushes into tracking events */
  readonly dataLayerBridge?: boolean;
  /** Custom dataLayer event names the customer explicitly asked for. */
  readonly dataLayerEvents?: readonly string[];
  /** Domains this funnel also lives on, declared by the customer. */
  readonly crossDomainHosts?: readonly string[];
  /** Pages the customer never wants measured; nothing is sent from them. */
  readonly excludedPages?: readonly string[];
  readonly pixelId?: string;
  readonly rules?: ReadonlyArray<TrackingRule>;
  readonly platforms?: PlatformConfig;
  readonly consent?: ConsentConfig;
  readonly serverCookies?: boolean;
  readonly cookieValues?: CookieValues;
  /** Visitor's GeoIP country (ISO alpha-2), used for E.164 phone normalization */
  readonly visitorCountry?: string;
  /**
   * Rules this visitor already converted on, decided server-side on the anchor
   * cookie. Absent means the server could not tell (unknown visitor, or the
   * lookup failed), which is NOT the same as an empty list: absent falls back
   * to whatever localStorage remembers.
   */
  readonly convertedRuleIds?: ReadonlyArray<string>;
}

export interface EventData {
  eventName?: string;
  /** Caller-supplied deterministic id (e.g. "ord_<order_id>") for dedup */
  eventId?: string;
  eventValue?: number;
  currency?: string;
  browserPixelFired?: boolean;
  customData?: Record<string, unknown>;
  trackingRuleId?: string;
  pageUrl?: string;
  source?: string;
}

/**
 * Record the LITERAL SDK invocation for the event-log payload viewer:
 * { method: "ttq.track", args: [positional arguments as passed], note? }.
 * The viewer renders this as the exact call (`ttq.track("Lead", {}, {...})`)
 * so a developer can compare it 1:1 against the platform's own docs; never
 * invent wrapper keys here, they read as bugs on the customer side.
 */
export function recordPixelPayload(
  payload: EventPayload,
  platform: string,
  data: Record<string, unknown>,
): void {
  if (!payload.pixelPayloads) payload.pixelPayloads = {};
  payload.pixelPayloads[platform] = data;
}

export interface EventPayload {
  websiteId: string;
  pageUrl: string;
  eventId: string;
  userAgent: string;
  fbp: string | null;
  fbc: string | null;
  externalId: string;
  /**
   * Device fingerprint hash. Absent unless consent allowed it to be measured
   * at all, and never sent as an empty string: an empty value shared by every
   * blocked visitor would be the worst possible match key.
   */
  fp?: string;
  timestamp: number;
  referrerUrl?: string;
  browserPixelFired: boolean;
  clickIds?: Record<string, string>;
  /**
   * The subset that was on the address bar when this page loaded, which is what
   * may decide the source of the visit. The cookie values above keep going to
   * the ad platforms either way. See getArrivalClickIds.
   */
  arrivalClickIds?: Record<string, string>;
  /** utm_* and ESP params from the landing URL; see tracking/source-params.ts */
  sourceParams?: Record<string, string>;
  pixelsFired: string[];
  /**
   * Conflicts observed on the page, such as another tool putting a conversion
   * on our pixel. Left off the payload entirely when there are none.
   */
  pixelConflicts?: string[];
  /** Exact props handed to each platform SDK (event-log "Browser" column) */
  pixelPayloads?: Record<string, Record<string, unknown>>;
  eventName?: string;
  eventValue?: number;
  currency?: string;
  customData?: Record<string, unknown>;
  trackingRuleId?: string;
  userData?: Record<string, unknown> & { googleSession?: GoogleSessionDetails };
  source?: string;
  /** Resolved consent state at send time (Google consent signals downstream) */
  consent?: { analytics: string; marketing: string };
  /** Waar die toestemming vandaan kwam: "cmp", "declared" of "default". */
  consentSource?: string;
  /** Browser locale (BCP 47), a cheap TikTok match signal */
  locale?: string;
  /** Screen size in CSS pixels, a cheap Reddit match signal */
  screen?: { w: number; h: number };
  _bufferedAt?: number;
}

export interface ClickIdConfig {
  readonly cookie: string;
  readonly format: ((value: string) => string) | null;
}

export interface DataAttributeConfig {
  readonly key: string;
  readonly parse: (value: string) => unknown;
}

export interface SelectorResult {
  sessionId: string;
  cssSelector: string;
  elementText: string;
  pageUrl: string;
}

/** Wat een site over toestemming kan zeggen. */
export type ConsentChoice = "granted" | "denied";

export interface PublicApi {
  init(websiteId: string): void;
  pageView(): void;
  track(
    eventName: string,
    valueOrParams?: number | Record<string, unknown>,
    currency?: string,
  ): void;
  trackWithPixel(eventName: string, value?: number, currency?: string): void;
  /** An e-commerce event from a source that knows; takes the first-wins claim. */
  trackEcommerce(eventName: string, params?: Record<string, unknown>, actionId?: string): void;
  setUserData(data: Record<string, string>): Promise<void>;
  /**
   * De keuze uit je eigen cookiebanner doorgeven.
   *
   * Voor sites met een zelfgebouwde banner, en dat zijn de meeste. Zonder dit
   * kunnen wij alleen Cookiebot, OneTrust en Google Consent Mode lezen, en
   * blijft er voor de rest niets over dan altijd meten of nooit meten.
   *
   * Een enkele waarde geldt voor allebei: `consent("granted")`.
   */
  consent(
    next: ConsentChoice | { analytics?: ConsentChoice; marketing?: ConsentChoice },
  ): void;
  getConfig(): Config | null;
  checkUrl(url?: string): void;
  isReady(): boolean;
}
